import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FeatureHero } from '../../../../widgets/feature-hero/feature-hero';
import { FaqsComponent } from '../../../../widgets/faqs/faqs.component';
import { FaqItem } from '../../../../core/interface/faq.modal';
import { FeatureRow } from '../../../../widgets/feature-row/feature-row';

@Component({
  selector: 'app-software-asset-management',
  templateUrl: './page.html',
  styleUrl: './page.scss',
  standalone: true,
  imports: [CommonModule, RouterModule, FeatureHero, FaqsComponent, FeatureRow],
})
export class AssetSAM {
  whatIsParagraphs = [
    "Software Asset Management (SAM) refers to the systematic tracking, management, and optimization of software assets, from procurement and deployment to maintenance and decommissioning. For instance, SAM helps automate the identification and monitoring of software installations with analytics on usage patterns.",
    "A key component of software asset management is integrating with the existing IT ecosystem, including CMDB and ERP systems. Software needs must also be predicted, license usage optimized, and future requirements forecasted."
  ];

  solutionParagraphs = [
    "Infraon's SAM solution offers a proactive approach to managing software asset management lifecycles. One of its features is automated discovery and inventory management, which scans the IT environment to catalog all installed software. It also empowers IT managers to generate insights into asset utilization.",
    "Infraon's SAM solution makes it easy to reconcile usage data with purchased licenses while identifying potential compliance risks. It comes with role-based access control and audit trails to safeguard sensitive data.",
    "Simply put, you can create a more reliable and streamlined software asset ecosystem."
  ];

  otherFeaturesItems = [
    'Customizable Reporting',
    'User and Role Management',
    'Vendor Management',
    'Audit Trails',
    'Mobile Accessibility'
  ];

  acquisitionParagraph = ["There are several steps involved in the first stage of acquiring assets. Infraon's software asset management solution helps:"];
  acquisitionItems = ['Identify requirements', 'Evaluate options', 'Select suitable vendors', 'Negotiate contracts'];

  installationParagraph = ["Once procured, the software is deployed and installed. Thanks to Infraon's software asset management solution, you can:"];
  installationItems = ['Configure software', 'Ensure compatibility', 'Implement monitoring', 'Provide support'];

  usageParagraph = ["Keeping the assets running smoothly is critical. Infraon's software asset management solution equips you to:"];
  usageItems = ['Enable updates/patches', 'Monitor software usage', 'Optimize performance', 'Maximize utilization'];

  auditParagraph = ["Ensuring asset auditing and compliance is a crucial need. Infraon's IT software asset management solution helps:"];
  auditItems = ['Conduct regular audits', 'Ensure regulatory adherence', 'Manage compliance', 'Maintain usage records'];

  disposalParagraph = ["It's vital to securely and responsibly dispose of obsolete assets. With Infraon's IT software asset management, you can easily:"];
  disposalItems = ['Plan decommissioning', 'Transfer or delete data', 'Terminate licenses', 'Update records'];

  bestPracticesParagraph = ["Some of the best practices that drive Infraon's software asset management solution are:"];
  bestPracticesItems = [
    'Automated software discovery across the organization',
    'Single repository for all software licenses and related documentation',
    'Optimized license allocation to identify and reallocate underutilized licenses',
    'Clear procurement processes for software purchase',
    'Contract and vendor management to maximize value and minimize risks',
    'Incident response and recovery for software-related issues'
  ];

  benefitsParagraph = ["Implementing Infraon's software asset management solution leads to business benefits such as:"];
  benefitsItems = [
    'Significant cost reductions by eliminating redundant or underutilized licenses',
    'Adherence to software licensing agreements and regulatory requirements',
    'Reduced manual effort and improved staff productivity',
    'Data-rich decisions on software investments and usage',
    'Protection against unauthorized access and cyber threats'
  ];

  faqs: FaqItem[] = [
    {
      question: 'What is Software Asset Management?',
      answer: {
        paragraphs: ['Software asset management (SAM) helps businesses optimize their budgets by actively governing and automating procurement, usage, and deployment of software licenses and subscriptions. When an employee moves to other locations/teams within the organization, the asset is suitably tagged to ensure that the employee’s location/team is updated in the Software Asset Management database. Regular application and software upgrades happen accordingly.']
      }
    },
    {
      question: 'What is the need for Software Asset Management solution?',
      answer: {
        paragraphs: ['A SAM program can help the IT administrators identify the number of licenses they have for each software vendor and are being used. It can then provide the SAM team with insights into the unused desktops and laptops and help them plan their inventory better. It also gives an overview of any software license breach caused by an employee or third-party contractor to whom the asset was allocated.']
      }
    },
    {
      question: 'How can you benefit from a Software Asset Management (SAM) solution?',
      answer: {
        paragraphs: ['Since the average enterprise uses 500+ software applications, manually keeping track of the users may be cumbersome and cause compliance failures. SAM solutions help administrators to save costs for the company. Proper visibility into the company’s software portfolio allows them to identify multiple opportunities for IT savings. It reduces the risk of software license audit failures and penalties. Better asset utilization and more robust security protocols can be maintained.']
      }
    },
    {
      question: 'What are the standard features of a Software Asset Management (SAM) solution?',
      answer: {
        paragraphs: ['A robust SAM solution should be able to track any software from the minute it is requested, through procurement, deployment, recycling, and finally, retirement. There are five types of software licenses - public domain, permissive, LGPL, copyleft, and proprietary. A good SAM solution should be able to keep track of all the software in its inventory and what type of license they have and manage their entire lifecycle. The key features of a robust SAM solution are - automated software discovery, software license management, software whitelisting and blacklisting, software true-up cost, vendor management, integrated ticketing, low stock alerts, and access control.']
      }
    }
  ];

  constructor() {}

}